import api from '../services/api';

// Web Push subscription helper.
//
// Flow:
//   1. isSupported() — check the browser actually has SW + PushManager
//      + Notification API. Old Edge, Firefox-on-iOS, etc. miss one.
//   2. getPermission() — current state without prompting.
//   3. subscribe() — request permission if needed, fetch the VAPID
//      public key, call PushManager.subscribe, POST to backend.
//   4. unsubscribe() — local PushManager.unsubscribe + DELETE on the
//      backend.
//
// All calls are idempotent: re-subscribing on a device that's already
// subscribed re-syncs the row in case the user signed in as someone
// else (the backend upserts on endpoint).

export function isSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// iOS only delivers web push when the site is launched as an installed
// PWA from the home screen. In a normal Safari tab, `'PushManager' in
// window` is true (the API is exposed) but `requestPermission()` and
// `pushManager.subscribe()` silently no-op. Callers gate their UI on
// canActuallySubscribe() so iOS users don't tap "Enable" only to see
// nothing happen.
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.navigator?.standalone === true;
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

export function canActuallySubscribe() {
  if (!isSupported()) return false;
  if (isIOS() && !isStandalone()) return false;
  return true;
}

export function getPermission() {
  if (!isSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// VAPID keys come from the server as base64url. PushManager.subscribe
// wants a Uint8Array — this conversion is a one-liner the API requires
// us to do ourselves.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  // Wait for the SW we registered in index.js to be ready. Returns
  // null in dev (no SW registered) so callers can short-circuit.
  if (!isSupported()) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

// Returns 'granted' | 'denied' | 'default' (user dismissed) — same
// values the Notification API uses, so callers can branch the UI.
export async function requestPermission() {
  if (!isSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

// Subscribe this device. Returns { ok: true } on success, or
// { ok: false, reason } where reason is one of:
//   'unsupported' | 'permission-denied' | 'no-vapid-key' | 'subscribe-failed'
export async function subscribe() {
  if (!isSupported()) return { ok: false, reason: 'unsupported' };

  const perm = await requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'permission-denied' };

  const reg = await getRegistration();
  if (!reg) return { ok: false, reason: 'unsupported' };

  // Pull the server's VAPID key. If push isn't configured server-side
  // (missing env vars), the endpoint returns 503 — surface that
  // distinctly so a settings UI can say "push isn't enabled yet"
  // rather than a generic error.
  let publicKey;
  try {
    const r = await api.get('/push/vapid-key');
    publicKey = r.data?.publicKey;
    if (!publicKey) return { ok: false, reason: 'no-vapid-key' };
  } catch {
    return { ok: false, reason: 'no-vapid-key' };
  }

  let subscription;
  try {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  } catch (err) {
    console.warn('PushManager.subscribe failed', err);
    return { ok: false, reason: 'subscribe-failed' };
  }

  try {
    const json = subscription.toJSON();
    await api.post('/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
    });
    return { ok: true };
  } catch (err) {
    console.warn('Backend subscribe save failed', err);
    // We still have a live PushManager subscription; rolling it back
    // would just leave the user permission-granted but unsubscribed
    // server-side. Better to leave the local subscription and let a
    // retry sync it.
    return { ok: false, reason: 'subscribe-failed' };
  }
}

export async function unsubscribe() {
  const reg = await getRegistration();
  if (!reg) return { ok: false, reason: 'unsupported' };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true }; // already gone

  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* best-effort */ }
  try { await api.delete('/push/subscribe', { data: { endpoint } }); } catch { /* best-effort */ }
  return { ok: true };
}

// Returns true if this device has an active subscription (cheap; reads
// the SW registration, no network).
export async function isSubscribed() {
  const reg = await getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
